import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { LookupKey } from '../../../../../shared/constants/lookup-keys';
import { SHARED_COMPONENTS } from '../../../../../shared/shared-imports';
import { Utils } from '../../../../../Utils/utils';
import { MultiSelectOption } from '../../../../../shared/models/select-option.model';
import { debounceTime, distinctUntilChanged, tap, filter, switchMap, finalize } from 'rxjs';
import { ApiRoot, ApiEndPoints } from '../../../../../shared/constants/api-endpoints';
import { HSCodeRequest } from '../../../../../shared/models/hs-code-request.model';
import { ApiService } from '../../../../../core/services/api.service';
import { SharedService } from '../../../../../shared/services/shared.service';
import { customValidator } from '../../../../../Utils/validator';

@Component({
  selector: 'app-marketing-product-form',
  imports: [ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS],
  templateUrl: './marketing-product-form.component.html',
  styleUrl: './marketing-product-form.component.scss'
})
export class MarketingProductFormComponent {
  @Input() isOpen = false;
  @Input() selectedProduct: any = "";
  @Input() products: any[] = [];
  @Input() srSubType: any = ""
  @Input() defaultHsCode: string | null = null;
  hsCodeList: MultiSelectOption[] = [];
  loadingHsCode: boolean = false;
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() onBtnClick = new EventEmitter<any>();

  form: FormGroup;
  countries: any = [];
  cities: any = [{ label: "Dubai", value: 1 }];
  lookupKey: any = LookupKey;
  isGSPCertificate: boolean = false;
  search$ = new EventEmitter<any>();
  hsCodeRequest: HSCodeRequest = new HSCodeRequest();
  lang: string = "en";
  addedProducts: any[] = [];
  isEditMode: boolean = false;

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private sharedService: SharedService
  ) {
    this.lang = this.sharedService.getCurrentLang();
    this.form = this.fb.group({
      id: [''],
      masterProductId: [''],
      kindOfGoods: ['', [Validators.required, Validators.maxLength(100), customValidator('english')]],
      productHSCode: [null, [Validators.required]],
      countryOfOrigin: [null, [Validators.required]]
    });
  }
  // This method is used to handle the on changes lifecycle hook using changes
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedProduct']) {
      this.isEditMode = !!this.selectedProduct;
      if (this.selectedProduct) {
        const fields = this.selectedProduct.formFields ? this.selectedProduct.formFields : this.selectedProduct;
        this.ensureHsCodeOption(fields?.productHSCode, fields?.kindOfGoods);
        this.form.patchValue({
          ...fields,
          id: this.selectedProduct?.id || fields?.id || ''
        });
      } else {
        this.resetForm();
      }
    }

    if (changes['products'] && !this.isEditMode) {
      this.addedProducts = [...(this.products || [])];
    }

    if (changes['defaultHsCode'] && this.defaultHsCode && !this.selectedProduct) {
      this.setDefaultHsCode(this.defaultHsCode);
    }
  }
  // This method is used to handle the on init lifecycle hook
  ngOnInit() {
    this.loadInitialHsCodes();
    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term: string) => term.length > 2),
        tap(() => (this.loadingHsCode = true)),
        switchMap((term: string) =>
          this.getHSCodeBySearch(term)
            .pipe(finalize(() => (this.loadingHsCode = false)))
        )
      )
      .subscribe((response: any) => {
        this.populateHsListFromResponse(response, false);
      })

    this.addedProducts = [...(this.products || [])];
    if (this.defaultHsCode && !this.selectedProduct) {
      this.setDefaultHsCode(this.defaultHsCode);
    }
  }

  // This method is used to get hs code by search using search text
  totalHsRecords: number = 0;
  hsCodeRowNum: number = 0;
  hsCodeSearchText: string = "";
  pageSize: any = 50;
  getHSCodeBySearch(searchText: string = "", startRow: number = 0, append: boolean = false) {
    this.hsCodeRowNum = startRow;
    this.hsCodeSearchText = searchText.trim();
    this.totalHsRecords = 0;
    this.hsCodeRequest.body.IncomingHierarchy.MessageId = Utils.generateUniqueId();
    this.hsCodeRequest.body.StartRowNum = this.hsCodeRowNum;
    this.hsCodeRequest.body.NewQuery = this.hsCodeRowNum == 0 ? "true" : "false";
    this.hsCodeRequest.body.PageSize = this.pageSize;
    this.hsCodeRequest.body.DCLanguage = this.lang == 'ar' ? 'ARA' : 'ENU';

    const product = this.hsCodeRequest.body.IncomingHierarchy.ListOfDCHSCODELIST.PRODUCT;
    // Reset both fields
    product.ProductCode = "";
    product.Description = "";

    // Check if search text is numeric only
    const isNumericSearch = /^\d+$/.test(this.hsCodeSearchText);
    if (isNumericSearch) {
      product.ProductCode = this.hsCodeSearchText;
    } else {
      product.Description = this.hsCodeSearchText.toUpperCase();
      product.ProductNameARA = "";
    }
    this.loadingHsCode = true;
    if (!append) {
      this.hsCodeList = [];
    }

    return this.api.post(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.GET_HS_CODE,
      this.hsCodeRequest
    )
  }

  // This method is used to handle search using term
  handleSearch(searchTerm: any) {
    const searchText = (typeof searchTerm === 'string'
      ? searchTerm
      : (searchTerm?.term ?? '')
    ).trim();

    if (!searchText) {
      this.loadInitialHsCodes();
      return;
    }

    this.search$.next(searchText);
  }

  private populateHsListFromResponse(response: any, append: boolean = false) {
    if (response?.SiebelMessage && response?.SiebelMessage['PRODUCT']) {
      this.totalHsRecords = response.TotalRecords;
      let hsProducts = Utils.checkIsArray(response?.SiebelMessage['PRODUCT']);
      const mapped = hsProducts.map((hs, index) => {
        return {
          id: (append ? this.hsCodeList.length : 0) + index + 1,
          code: hs.ProductCode,
          name: hs.Description,
          description: hs.ProductNameARA,
        } as MultiSelectOption;
      });
      this.hsCodeList = append ? [...this.hsCodeList, ...mapped] : mapped;
    }
  }

  private loadInitialHsCodes() {
    this.getHSCodeBySearch("", 0, false)
      .pipe(finalize(() => (this.loadingHsCode = false)))
      .subscribe((response: any) => {
        this.populateHsListFromResponse(response, false);
      });
  }

  // This method is used to fetch next record using page
  fetchNextRecord(page: any) {
    const startRow = Number(this.pageSize) * (page - 1);
    this.getHSCodeBySearch(this.hsCodeSearchText || "", startRow, true)
      .pipe(finalize(() => (this.loadingHsCode = false)))
      .subscribe((response: any) => {
        this.populateHsListFromResponse(response, true);
      });
  }

  selectedItems: any = []
  // This method is used to handle add item event using items
  onAddItem(items: any) {
    this.selectedItems = [];
    if (items.length > 0) {
      this.selectedItems = items;
      let firstIndex = this.selectedItems[this.selectedItems.length - 1]
      this.form.get('productHSCode')?.setValue(firstIndex.code);
      this.form.get('productHSCode')?.updateValueAndValidity();
    } else {
      this.form.get('productHSCode')?.setValue("");
      this.form.get('productHSCode')?.updateValueAndValidity();
    }
  }
  // This method is used to handle remove item event using items
  onRemoveItem(items: any) {
    if (items?.length < 1) {
      this.selectedItems = items;
      this.form.get('productHSCode')?.setValue('');
      // this.loadInitialHsCodes();
    }
  }
  // This method is used to clear hs code
  clearHsCode() {
    this.hsCodeSearchText = "";
    this.hsCodeRowNum = 0;
    this.totalHsRecords = 0;
    this.selectedItems = [];
    if (!this.hsCodeList || this.hsCodeList?.length < 1) {
      this.loadInitialHsCodes();
    }

  }
  private resetForm() {
    this.form.reset({
      id: '',
      kindOfGoods: '',
      productHSCode: null,
      countryOfOrigin: null
    });
  }

  private ensureHsCodeOption(code: string | null | undefined, label?: string) {
    if (!code) return;
    const exists = this.hsCodeList.some((h) => h.code === code);
    if (!exists) {
      this.hsCodeList = [
        { id: this.hsCodeList.length + 1, code, name: label || code, description: '' },
        ...this.hsCodeList
      ];
    }
  }

  private setDefaultHsCode(code: string) {
    this.ensureHsCodeOption(code);
    this.form.get('productHSCode')?.setValue(code);
    this.form.get('productHSCode')?.updateValueAndValidity({ emitEvent: false });
  }

  // This method is used to add a new product to the in-modal list
  add() {
    this.form.markAllAsTouched();
    const invalidControls = Utils.getInvalidControls(this.form);
    console.warn('Invalid controls:', invalidControls);
    if (this.form.valid) {
      const productId = this.form.value.id || Utils.generateUniqueId();
      const newProduct = {
        ...this.form.value,
        id: productId,
        formFields: { ...this.form.value, id: productId }
      };
      this.addedProducts = [
        ...this.addedProducts.filter((p: any) => p.id !== newProduct.id),
        newProduct
      ];
      this.resetForm();
      this.selectedItems = [];
    }
  }

  removeProduct(product: any) {
    this.addedProducts = this.addedProducts.filter((p: any) => p.id !== product.id);
  }

  saveAndClose() {
    this.form.markAllAsTouched();
    if (this.isEditMode) {
      if (this.form.invalid) {
        return;
      }
      const productId = this.selectedProduct?.id || this.form.value.id || Utils.generateUniqueId();
      const updatedProduct = {
        ...this.selectedProduct,
        ...this.form.value,
        id: productId,
        formFields: { ...this.form.value, id: productId }
      };
      this.onBtnClick.emit({ type: 'update', product: updatedProduct });
      this.close();
      return;
    }

    this.add();
    if (this.addedProducts.length === 0) {
      return;
    }
    this.onBtnClick.emit({ type: 'add', products: this.addedProducts });
    this.close();
  }

  // This method is used to close
  close() {
    this.isEditMode = false;
    this.isOpenChange.emit(false);
    this.isOpen = false;
  }
}


